import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useExportAllConfigEntityMutation,
  useExportConfigEntityMutation,
  useImportConfigEntityMutation,
} from '@redux/api'

import { useAppDispatch } from '@hooks'
import { showSnackbarNotification } from '@helpers'
import { CONFIG_MENU_OPTION, ENTITY_CONFIG_TYPE } from '@constants'

export type UseEditConfigMenu = {
  configMenuOption: CONFIG_MENU_OPTION | null
  handleSelectConfigMenuOption: (
    option: CONFIG_MENU_OPTION | null,
    configId?: number | string
  ) => void
  selectedConfigId: number | null
  handleSelectConfigId: (id: number) => void
  isExportAllSelected: boolean
  handleSelectExportAll: (checked: boolean) => void
  handleExportConfigs: (configId?: number) => void
  configFileText: string
  handleFileUpload: (file: File | null) => Promise<void>
  handleApply: (value: string) => Promise<void>
  isOpenScriptDialog: boolean
  isConfigMenuLoading: boolean
  setIsOpenScriptDialog: (open: boolean) => void
  handleCancel: () => void
  handleCopyScript: () => void
  entityType: ENTITY_CONFIG_TYPE
}

export const useEditConfigMenu = (configType: ENTITY_CONFIG_TYPE): UseEditConfigMenu => {
  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  const [configMenuOption, setConfigMenuOption] = useState<CONFIG_MENU_OPTION | null>(null)
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null)
  const [isExportAllSelected, setIsExportAllSelected] = useState(false)
  const [configFileText, setConfigFileText] = useState<string>('')
  const [isOpenScriptDialog, setIsOpenScriptDialog] = useState(false)
  const [exportConfigEntity, { isLoading: isExportConfigLoading }] = useExportConfigEntityMutation()
  const [exportAllConfigEntity, { isLoading: isExportAllConfigLoading }] =
    useExportAllConfigEntityMutation()

  const [importConfigEntity, { isLoading: isImportConfigLoading }] = useImportConfigEntityMutation()

  const isConfigMenuLoading =
    isExportConfigLoading || isExportAllConfigLoading || isImportConfigLoading

  const handleSelectConfigMenuOption = (
    option: CONFIG_MENU_OPTION | null,
    configId?: number | string
  ) => {
    setConfigMenuOption(option)

    if (configType && configId) {
      handleExportConfigs(configId)
    }
  }

  const handleSelectConfigId = (id: number) => {
    setIsExportAllSelected(false)
    setSelectedConfigId(id)
  }

  const handleSelectExportAll = (checked: boolean) => {
    setIsExportAllSelected(checked)
    setSelectedConfigId(null)
  }

  const handleExportConfigs = (configId?: number | string) => {
    if (isExportAllSelected) {
      exportAllConfigEntity?.({ configCode: configType })
        .unwrap()
        .then(text => {
          setConfigFileText(text)
          setIsOpenScriptDialog(true)
        })
        .catch(err => {
          console.error('Export config error:', err)
        })
    } else {
      if (!selectedConfigId && !configId) return
      const id = selectedConfigId || configId
      exportConfigEntity?.({ configCode: configType, id })
        .unwrap()
        .then(text => {
          setConfigFileText(text)
          setIsOpenScriptDialog(true)
        })
        .catch(err => {
          console.error('Export config error:', err)
        })
    }
  }

  const handleFileUpload = async (file: File | null) => {
    if (file) {
      const text = await file.text()
      setConfigFileText(text)
    }
  }

  const handleCancel = () => {
    setConfigFileText('')
    setSelectedConfigId(null)
    setConfigMenuOption(null)
    setIsOpenScriptDialog(false)
  }

  const handleCopyScript = () => {
    showSnackbarNotification({
      text: t('ganttCreate.resourceForm.additionalFieldsForm.copyAlert'),
      options: {
        variant: 'success',
      },
    })
  }

  const handleApply = async value => {
    if (configMenuOption === CONFIG_MENU_OPTION.EXPORT) {
      if (!value) return

      const doc = new DOMParser().parseFromString(value, 'text/xml')

      const configuration = doc.querySelector('configuration')
      const container = configuration?.firstElementChild
      const firstItem = container?.firstElementChild
      const title = firstItem?.getAttribute('title') || 'config'

      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${title}.xml`,
          types: [
            {
              description: 'XML File',
              accept: { 'text/xml': ['.xml'] },
            },
          ],
        })

        const writable = await handle.createWritable()
        await writable.write(value)
        await writable.close()
      } catch (err) {
        console.error('Save cancelled or failed', err)
      }
    } else {
      importConfigEntity(value)
    }

    handleCancel()
  }

  return {
    configMenuOption,
    handleSelectConfigMenuOption,
    selectedConfigId,
    handleSelectConfigId,
    isExportAllSelected,
    handleSelectExportAll,
    handleExportConfigs,
    configFileText,
    handleFileUpload,
    handleApply,
    isOpenScriptDialog,
    setIsOpenScriptDialog,
    handleCancel,
    handleCopyScript,
    isConfigMenuLoading,
  }
}
